import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getUserDetails,
  fetchDepartmentOptions,
  fetchJobRoleOptions,
  fetchPurposeOptions,
} from "../../utils/api";
import FocusedFlowPage from "../../components/FocusedFlowPage";
import { ADMIN_ROUTES } from "../../constants/routes";

const maroon = "#500000";

const EditUserAccount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    department: [],
    jobRole: [],
    purposeForAccess: [],
    admin: "",
    tamuTeam: "",
  });
  const [originalData, setOriginalData] = useState({});
  const [departments, setDepartments] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get data from location state
  const { user, reason, comments, returnTo } = location.state || {};

  const loadUserData = useCallback(async () => {
    if (!user?.userId) return;

    setLoading(true);
    setError(null);

    try {
      const userDetails = await getUserDetails(user.userId);
      const userData = userDetails || user;

      // Debug: Log the user data structure
      console.log("User data structure:", userData);
      console.log(
        "Departments:",
        userData.departments,
        "Department:",
        userData.department
      );
      console.log(
        "Job Roles:",
        userData.jobRoles,
        "Job Role:",
        userData.jobRole
      );

      setFormData({
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        email: userData.email || "",
        department: Array.isArray(userData.departments)
          ? userData.departments
          : userData.departments
          ? [userData.departments]
          : Array.isArray(userData.department)
          ? userData.department
          : userData.department
          ? [userData.department]
          : [],
        jobRole: Array.isArray(userData.jobRoles)
          ? userData.jobRoles
          : userData.jobRoles
          ? [userData.jobRoles]
          : Array.isArray(userData.jobRole)
          ? userData.jobRole
          : userData.jobRole
          ? [userData.jobRole]
          : [],
        purposeForAccess: Array.isArray(userData.purposeForAccess)
          ? userData.purposeForAccess
          : userData.purposeForAccess
          ? [userData.purposeForAccess]
          : [],
        admin: userData.admin || "",
        tamuTeam: userData.tamuTeam || "",
      });

      setOriginalData({
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        email: userData.email || "",
        department: Array.isArray(userData.departments)
          ? userData.departments
          : userData.departments
          ? [userData.departments]
          : Array.isArray(userData.department)
          ? userData.department
          : userData.department
          ? [userData.department]
          : [],
        jobRole: Array.isArray(userData.jobRoles)
          ? userData.jobRoles
          : userData.jobRoles
          ? [userData.jobRoles]
          : Array.isArray(userData.jobRole)
          ? userData.jobRole
          : userData.jobRole
          ? [userData.jobRole]
          : [],
        purposeForAccess: Array.isArray(userData.purposeForAccess)
          ? userData.purposeForAccess
          : userData.purposeForAccess
          ? [userData.purposeForAccess]
          : [],
        admin: userData.admin || "",
        tamuTeam: userData.tamuTeam || "",
      });
    } catch (err) {
      console.error("Error loading user data:", err);
      setError("Failed to load user data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.userId) {
      loadUserData();
      loadDropdownData();
    }
  }, [user, loadUserData]);

  const loadDropdownData = async () => {
    try {
      const [deptData, jobData, purposeData] = await Promise.all([
        fetchDepartmentOptions(),
        fetchJobRoleOptions(),
        fetchPurposeOptions(),
      ]);

      setDepartments(deptData || []);
      setJobRoles(jobData || []);
      setPurposes(purposeData || []);
    } catch (err) {
      console.error("Error loading dropdown data:", err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    // Navigate to confirmation page
    navigate(ADMIN_ROUTES.USER_CONFIRM, {
      state: {
        user,
        reason,
        comments,
        updatedData: formData,
        originalData,
        returnTo,
        step: "confirm",
      },
    });
  };

  const handleBack = () => {
    navigate(ADMIN_ROUTES.USER_EDIT_REASON, {
      state: { user, returnTo },
    });
  };

  const handleCancel = () => {
    // Navigate back to the specified return path, or user profile, or user management
    if (returnTo) {
      navigate(returnTo);
    } else if (user && user.id) {
      navigate(ADMIN_ROUTES.USER_PROFILE(user.id));
    } else {
      // Fallback to user management if no user data
      navigate(ADMIN_ROUTES.USER_MANAGEMENT);
    }
  };

  const hasChanges = () => {
    return Object.keys(formData).some((key) => {
      const current = formData[key];
      const original = originalData[key];

      // Handle array comparisons for multi-select fields
      if (Array.isArray(current) && Array.isArray(original)) {
        return (
          current.length !== original.length ||
          !current.every((item, index) => item === original[index])
        );
      }

      return current !== original;
    });
  };

  if (!user) {
    return (
      <FocusedFlowPage
        title="Edit User Account Information"
        maxWidth={700}
        actions={[
          <Button
            key="back"
            onClick={() => navigate(ADMIN_ROUTES.USER_MANAGEMENT)}
            variant="contained"
            sx={{ backgroundColor: maroon }}
          >
            Back to User Management
          </Button>,
        ]}
      >
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="error">
            No user data found. Please go back to user management.
          </Typography>
        </Box>
      </FocusedFlowPage>
    );
  }

  return (
    <FocusedFlowPage
      title="Edit User Account Information"
      subtitle={`Edit the account information for ${user.firstName} ${user.lastName}.`}
      onClose={handleCancel}
      maxWidth={860}
      actions={[
        <Button
          key="back"
          onClick={handleBack}
          variant="outlined"
          sx={{
            borderColor: "#ccc",
            color: "#666",
            "&:hover": {
              borderColor: "#999",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          Back
        </Button>,
        <Button
          key="cancel"
          onClick={handleCancel}
          variant="outlined"
          sx={{
            borderColor: "#ccc",
            color: "#666",
            "&:hover": {
              borderColor: "#999",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          Cancel
        </Button>,
        <Button
          key="save"
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges() || loading}
          sx={{
            backgroundColor: maroon,
            "&:hover": {
              backgroundColor: "#4a0000",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
              color: "#666",
            },
            fontWeight: 600,
            px: 3,
          }}
        >
          SAVE & UPDATE
        </Button>,
      ]}
    >
      <Box sx={{ maxWidth: "800px", mx: "auto" }}>
        {/* Form */}
        <Paper sx={{ p: 4, mb: 3 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: maroon }} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}
            >
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: maroon,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: maroon,
                    },
                  },
                }}
              />

              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: maroon,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: maroon,
                    },
                  },
                }}
              />

              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: maroon,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: maroon,
                    },
                  },
                }}
              />

              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  multiple
                  value={formData.department}
                  onChange={(e) =>
                    handleInputChange("department", e.target.value)
                  }
                  input={<OutlinedInput label="Department" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: maroon,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: maroon,
                      },
                    },
                  }}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id || dept} value={dept.name || dept}>
                      {dept.name || dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Job Role</InputLabel>
                <Select
                  multiple
                  value={formData.jobRole}
                  onChange={(e) => handleInputChange("jobRole", e.target.value)}
                  input={<OutlinedInput label="Job Role" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: maroon,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: maroon,
                      },
                    },
                  }}
                >
                  {jobRoles.map((role) => (
                    <MenuItem key={role.id || role} value={role.name || role}>
                      {role.name || role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Purpose for Access</InputLabel>
                <Select
                  multiple
                  value={formData.purposeForAccess}
                  onChange={(e) =>
                    handleInputChange("purposeForAccess", e.target.value)
                  }
                  input={<OutlinedInput label="Purpose for Access" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: maroon,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: maroon,
                      },
                    },
                  }}
                >
                  {purposes.map((purpose) => (
                    <MenuItem
                      key={purpose.id || purpose}
                      value={purpose.name || purpose}
                    >
                      {purpose.name || purpose}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Admin</InputLabel>
                <Select
                  value={formData.admin}
                  onChange={(e) => handleInputChange("admin", e.target.value)}
                  label="Admin"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: maroon,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: maroon,
                      },
                    },
                  }}
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>TAMU Team</InputLabel>
                <Select
                  value={formData.tamuTeam}
                  onChange={(e) =>
                    handleInputChange("tamuTeam", e.target.value)
                  }
                  label="TAMU Team"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: maroon,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: maroon,
                      },
                    },
                  }}
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Paper>
      </Box>
    </FocusedFlowPage>
  );
};

export default EditUserAccount;
